import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TourBooking {
  id: string;
  booking_number: string;
  tour_id: string | null;
  tour_name: string;
  customer_name: string;
  email: string;
  phone: string;
  pickup_point: string;
  booking_date: string;
  tour_schedule: string;
  adults: number;
  total_price: number;
  payment_method: string;
  payment_id: string | null;
  payment_status: 'pending' | 'completed' | 'failed' | 'cancelled';
  booking_status: 'pending' | 'confirmed' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export const useTourBookings = () => {
  const [tourBookings, setTourBookings] = useState<TourBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTourBookings();
  }, []);

  const fetchTourBookings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tour_bookings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTourBookings((data || []) as TourBooking[]);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching tour bookings:', err);
      setError('Error loading tour bookings');
    } finally {
      setLoading(false);
    }
  };

  const createTourBooking = async (bookingData: {
    tour_id?: string;
    tour_name: string;
    customer_name: string;
    email: string;
    phone: string;
    pickup_point: string;
    booking_date: string;
    tour_schedule: string;
    adults: number;
    total_price: number;
    payment_method: string;
    payment_id?: string;
    payment_status: 'pending' | 'completed' | 'failed' | 'cancelled';
    booking_status: 'pending' | 'confirmed' | 'cancelled';
  }) => {
    try {
      // Generate booking number client-side to avoid RLS issues
      const timestamp = Date.now();
      const randomDigits = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const clientBookingNumber = `TUR-${timestamp.toString().slice(-6)}${randomDigits}`;

      const insertData = {
        ...bookingData,
        booking_number: clientBookingNumber
      };

      const { error } = await supabase
        .from('tour_bookings')
        .insert(insertData);

      if (error) {
        console.error('Supabase error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      // Send confirmation email for all payment methods
      console.log('Sending confirmation email for tour booking:', insertData.booking_number);
      try {
        await sendTourConfirmationEmail(insertData);
      } catch (emailError) {
        console.error('Error sending tour confirmation email:', emailError);
        // Don't throw here - we still want the booking to be created even if email fails
      }
      
      await fetchTourBookings(); // Refresh the list
      
      // Return the booking data we just created
      return {
        id: crypto.randomUUID(), // Generate a temporary ID for navigation
        ...insertData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as TourBooking;
    } catch (err: any) {
      console.error('Error creating tour booking:', err);
      console.error('Error details:', {
        message: err?.message,
        details: err?.details,
        hint: err?.hint,
        code: err?.code
      });
      throw new Error(`Error creating tour booking: ${err?.message || 'Unknown error'}`);
    }
  };

  const sendTourConfirmationEmail = async (bookingData: any) => {
    try {
      // Get email configuration and tour template using correct keys
      const [emailEnabledResult, adminEmailResult, senderEmailResult, senderNameResult, templateResult] = await Promise.all([
        supabase
          .from('site_config')
          .select('value')
          .eq('key', 'email_enabled')
          .single(),
        supabase
          .from('site_config')
          .select('value')
          .eq('key', 'email_admin_email')
          .single(),
        supabase
          .from('site_config')
          .select('value')
          .eq('key', 'email_sender_email')
          .single(),
        supabase
          .from('site_config')
          .select('value')
          .eq('key', 'email_sender_name')
          .single(),
        supabase
          .from('site_config')
          .select('value')
          .eq('key', 'email_template_tour')
          .single()
      ]);

      const emailEnabled = emailEnabledResult.data?.value;
      const rawAdminEmail = adminEmailResult.data?.value;
      const rawSenderEmail = senderEmailResult.data?.value;
      const rawSenderName = senderNameResult.data?.value;
      const template = templateResult.data?.value as any;

      const normalize = (v: any) =>
        typeof v === 'string' ? v.trim().replace(/^(["'])(.*)\1$/g, '$2') : v;

      const adminEmail = normalize(rawAdminEmail);
      const senderEmail = normalize(rawSenderEmail);
      const senderName = normalize(rawSenderName);

      console.log('Tour email configuration:', { emailEnabled, adminEmail, senderEmail, senderName });
      console.log('Tour template:', template);

      if (!template?.is_active) {
        console.log('Tour email template is not active, skipping email');
        return;
      }

      if (!emailEnabled) {
        console.log('Email service is not enabled, skipping email');
        return;
      }

      // Call the edge function to send confirmation email
      console.log('Calling send-tour-confirmation edge function...');
      const { data, error } = await supabase.functions.invoke('send-tour-confirmation', {
        body: {
          booking_data: {
            booking_number: bookingData.booking_number,
            customer_name: bookingData.customer_name,
            email: bookingData.email,
            phone: bookingData.phone,
            tour_name: bookingData.tour_name,
            booking_date: bookingData.booking_date,
            tour_schedule: bookingData.tour_schedule,
            pickup_point: bookingData.pickup_point,
            adults: bookingData.adults.toString(),
            total_price: bookingData.total_price.toFixed(2)
          },
          template: {
            subject: template.subject,
            html_content: template.html_content,
            variables: template.variables
          },
          admin_email: adminEmail,
          sender_email: senderEmail,
          sender_name: senderName
        }
      });

      console.log('Tour edge function response:', { data, error });

      if (error) {
        console.error('Error calling send-tour-confirmation function:', error);
        throw error;
      }

      console.log('Tour confirmation email sent successfully');
    } catch (error) {
      console.error('Error in sendTourConfirmationEmail:', error);
      throw error;
    }
  };

  const updateBookingStatus = async (id: string, status: 'pending' | 'confirmed' | 'cancelled') => {
    try {
      const { error } = await supabase
        .from('tour_bookings')
        .update({ 
          booking_status: status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      await fetchTourBookings();
    } catch (err) {
      console.error('Error updating booking status:', err);
      throw new Error('Error updating booking status');
    }
  };

  const updatePaymentStatus = async (id: string, status: 'pending' | 'completed' | 'failed' | 'cancelled', paymentId?: string) => {
    try {
      const updateData: any = { 
        payment_status: status,
        updated_at: new Date().toISOString()
      };
      
      if (paymentId) {
        updateData.payment_id = paymentId;
      }

      const { error } = await supabase
        .from('tour_bookings')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
      await fetchTourBookings();
    } catch (err) {
      console.error('Error updating payment status:', err);
      throw new Error('Error updating payment status');
    }
  };

  const deleteBooking = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tour_bookings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchTourBookings();
    } catch (err) {
      console.error('Error deleting booking:', err);
      throw new Error('Error deleting booking');
    }
  };

  return {
    tourBookings,
    loading,
    error,
    refetch: fetchTourBookings,
    createTourBooking,
    updateBookingStatus,
    updatePaymentStatus,
    deleteBooking,
  };
};